import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  afterEveryRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import {
  AssignToButtonComponent,
  type AssignMember,
} from '@frontendlib/components/components/assign-to-button/assign-to-button.component';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import type {
  NoteCollaborationDto,
  NoteMemberProfileDto,
  NoteTeamAccess,
} from '@sharedlib/dtos/dto/notes-app/note-collaboration.dto';
import { avatarPalette } from '@sharedlib/common/utils/avatar-palette';
import { SignalType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { catchError, debounceTime, EMPTY, filter, fromEvent, map, Subject, switchMap, take } from 'rxjs';
import { SpaceStateService } from '@modules/common/services/space-state.service';
import type { Space } from '@modules/settings/types/space';
import { resolveSettingsRole } from '@modules/settings-window/settings-window-opener.service';
import {
  TeamNoteAccessClient,
  TEAM_NOTE_ACCESS_TOAST,
  teamNoteAccessError,
} from '@modules/team-note-access/team-note-access.client';
import { NoteCollaborationApiService } from './note-collaboration-api.service';
import { NoteAccessPanelComponent, type NoteAccessView } from './note-access-panel.component';

@Component({
  selector: 'app-note-collaboration-controls',
  imports: [AssignToButtonComponent, Button2Component, IconComponent, PopoverDirective, NoteAccessPanelComponent],
  providers: [TeamNoteAccessClient],
  templateUrl: './note-collaboration-controls.component.html',
  styleUrl: './note-collaboration-controls.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteCollaborationControlsComponent {
  public readonly noteId = input<string | null>(null);
  public readonly teamTenantId = input<string | null>(null);
  public readonly draft = input(false);
  public readonly teamName = input('Team Workspace');
  public readonly showAccessControl = input(true);
  public readonly showAssigneeControl = input(true);
  /** The trigger text with nobody assigned; the note window names it so it cannot be mistaken for the hand-over Assign. */
  public readonly assignLabel = input('Assign');
  protected readonly accessOpen = signal(false);
  protected readonly closeAccess = new Subject<void>();
  private readonly accessTrigger = viewChild<Button2Component>('accessTrigger');
  public readonly draftLocked = input(false);
  public readonly draftAssigneesChange = output<readonly string[]>();
  public readonly draftAccessChange = output<NoteTeamAccess>();
  public readonly collaborationChange = output<NoteCollaborationDto | null | undefined>();
  public readonly accessLost = output<void>();

  private readonly api = inject(NoteCollaborationApiService);
  private readonly injector = inject(Injector);
  private readonly spaceState = inject(SpaceStateService, { optional: true });
  private readonly currentSpace = this.spaceState
    ? toSignal(this.spaceState.getCurrentSpace(), { initialValue: null })
    : signal<Space | null>(null);
  /** The note whose Owner/Admin change the server has refused; the control is off for it until the context changes. */
  private readonly refusedAccessKey = signal<string | null>(null);
  /** A team Owner or Admin of THIS team manages the access of any team note, like the prototype's `manage`. */
  private readonly teamAdminAccess = computed(() => {
    const collaboration = this.collaboration();
    const space = this.currentSpace();
    const role = resolveSettingsRole(space, this.currentUser.accountId());
    return (
      !this.draft() &&
      this.loadState() === 'ready' &&
      !!collaboration &&
      space?.id === this.teamTenantId() &&
      (role === 'admin' || role === 'owner') &&
      this.refusedAccessKey() !== this.teamAccessKey(collaboration.ownerAccountUid)
    );
  });
  private readonly accountService = inject(AccountService);
  private readonly currentUser = inject(CurrentUserStore);
  private readonly document = inject(DOCUMENT);
  private readonly signals = inject(SignalsService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private detailRegion: HTMLElement | null = null;
  private detailObserver: IntersectionObserver | null = null;
  private detailIntersecting = false;
  private detailObserved = false;
  private initialReadPending = false;
  private visibleReadPending = false;
  private refreshAfterInitialRead = false;
  private refreshAfterVisibleRead = false;
  private contextEpoch = 0;
  private destroyed = false;
  private readonly searchTerms = new Subject<{ tenantId: string; actorId: string; search: string }>();
  private readonly profiles = signal<ReadonlyMap<string, NoteMemberProfileDto>>(new Map());
  private readonly selfProfile = signal<NoteMemberProfileDto | null>(null);
  private readonly roster = signal<readonly NoteMemberProfileDto[]>([]);
  private readonly memberCount = signal(0);
  private readonly memberSearch = signal('');
  private rosterSearch: string | null = null;
  private loadFailure: 'collaboration' | 'members' | 'memberPage' | null = null;
  protected readonly collaboration = signal<NoteCollaborationDto | null>(null);
  protected readonly loadState = signal<'loading' | 'ready' | 'legacy' | 'error'>('loading');
  private readonly requestedIds = signal<readonly string[] | null>(null);
  private requestedDelta: { added: readonly string[]; removed: readonly string[] } | null = null;
  private readonly draftIds = signal<readonly string[]>([]);
  private readonly draftAccess = signal<NoteTeamAccess>('editor');
  private readonly seenCycles = new Set<string>();
  protected readonly accountUid = signal('');
  protected readonly loading = signal(false);
  protected readonly loadingMore = signal(false);
  protected readonly pending = signal(false);
  protected readonly errorText = signal<string | null>(null);
  protected readonly visible = computed(() => !!this.teamTenantId() && (this.draft() || !!this.noteId()));
  protected readonly unavailableReason = computed(() => {
    if (this.loadState() === 'legacy') return 'Assignments are unavailable for notes created before collaboration.';
    if (this.loadState() === 'error') return 'Could not load assignment details. Retry to refresh.';
    return 'Loading assignment details.';
  });
  protected readonly currentAccess = computed(() => this.collaboration()?.access ?? this.draftAccess());
  protected readonly teamInitials = computed(() =>
    this.teamName()
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join(''),
  );
  protected readonly canChangeAccess = computed(() =>
    this.draft() ? !this.draftLocked() : !!this.collaboration()?.capabilities.canChangeAccess,
  );
  public readonly accessView = computed<NoteAccessView>(() => {
    const profile = this.draft() ? this.selfProfile() : this.collaboration()?.ownerProfile;
    const state = this.draft() ? (profile ? 'ready' : 'loading') : this.loadState();
    return {
      owner: profile
        ? {
            name: profile.name,
            email: profile.email,
            avatarUrl: profile.avatar ? Account.getAvatarUrl(profile.avatar, undefined, profile.accountUid) : undefined,
          }
        : null,
      access: state === 'ready' ? this.currentAccess() : null,
      state,
      editable: this.canChangeAccess() || this.teamAdminAccess(),
      pending: this.pending() || (this.draft() && this.draftLocked()),
      error: this.errorText(),
    };
  });
  protected readonly selectedIds = computed(
    () =>
      this.requestedIds() ??
      (this.draft() ? this.draftIds() : (this.collaboration()?.assignees.map((item) => item.accountUid) ?? [])),
  );
  protected readonly selectedMembers = computed<readonly AssignMember[]>(() => {
    const profiles = this.profiles();
    const self = this.selfProfile();
    return this.selectedIds().map((id) => {
      const profile = id === self?.accountUid ? self : profiles.get(id);
      return {
        id,
        name: profile?.name ?? 'Former member',
        avatarUrl: profile?.avatar ? Account.getAvatarUrl(profile.avatar, undefined, id) : undefined,
        avatarBackground: avatarPalette(id).background,
        avatarText: avatarPalette(id).text,
      };
    });
  });
  protected readonly members = computed<readonly AssignMember[]>(() => {
    const choices = this.capabilities().canAssign
      ? this.roster().map((profile) => ({
          id: profile.accountUid,
          name: profile.name,
          avatarUrl: profile.avatar ? Account.getAvatarUrl(profile.avatar, undefined, profile.accountUid) : undefined,
          avatarBackground: avatarPalette(profile.accountUid).background,
          avatarText: avatarPalette(profile.accountUid).text,
        }))
      : [...this.selectedMembers()];
    const viewer = this.selfProfile();
    if (!viewer || choices.some((member) => member.id === viewer.accountUid)) return choices;
    return [
      {
        id: viewer.accountUid,
        name: viewer.name,
        avatarUrl: viewer.avatar ? Account.getAvatarUrl(viewer.avatar, undefined, viewer.accountUid) : undefined,
        avatarBackground: avatarPalette(viewer.accountUid).background,
        avatarText: avatarPalette(viewer.accountUid).text,
      },
      ...choices,
    ];
  });
  protected readonly hasMore = computed(
    () => this.capabilities().canAssign && !this.loading() && this.roster().length < this.memberCount(),
  );
  protected readonly capabilities = computed(() =>
    this.draft()
      ? { canAssign: true, canResolve: false, canReopen: false }
      : (this.collaboration()?.capabilities ?? { canAssign: false, canResolve: false, canReopen: false }),
  );

  public constructor() {
    afterEveryRender(() => this.observeDetailRegion());
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.contextEpoch += 1;
      this.detailObserver?.disconnect();
      this.closeAccess.complete();
    });
    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.isDetailViewed()) this.refreshVisibleDetail();
      });

    this.signals
      .getSignal(SignalType.COLLABORATION_CHANGED)
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (
          event.stitchType !== StitchType.NOTE ||
          event.itemUid !== this.noteId() ||
          event.spaceId !== this.teamTenantId()
        )
          return;
        const noteId = this.noteId();
        const tenantId = this.teamTenantId();
        if (noteId && tenantId) this.reload(noteId, tenantId);
      });
    effect((onCleanup) => {
      const actorId = this.currentUser.accountId();
      this.accountUid.set(actorId ?? '');
      this.selfProfile.set(null);
      if (!actorId) return;
      const subscription = this.accountService
        .getAccount(true)
        .pipe(
          filter((account) => account.id === actorId),
          take(1),
        )
        .subscribe((account) => {
          this.selfProfile.set({
            accountUid: account.id,
            name: account.fullName.trim() || 'You',
            email: account.email,
            avatar: account.avatar,
          });
        });
      onCleanup(() => subscription.unsubscribe());
    });

    this.searchTerms
      .pipe(
        debounceTime(180),
        switchMap(({ tenantId, actorId, search }) => {
          if (
            tenantId !== this.teamTenantId() ||
            actorId !== this.currentUser.accountId() ||
            (!this.draft() && !this.collaboration()?.capabilities.canAssign)
          )
            return EMPTY;
          this.loading.set(true);
          return this.api.members(tenantId, search).pipe(
            map(({ items, count }) => ({ items, count, tenantId, actorId, search })),
            catchError(() => {
              if (
                actorId === this.currentUser.accountId() &&
                tenantId === this.teamTenantId() &&
                search === this.memberSearch()
              ) {
                this.loading.set(false);
                this.loadFailure = 'members';
                this.errorText.set('Could not load team members.');
              }
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ items, count, tenantId, actorId, search }) => {
        if (
          tenantId !== this.teamTenantId() ||
          actorId !== this.currentUser.accountId() ||
          search !== this.memberSearch()
        )
          return;
        this.roster.set(items);
        this.memberCount.set(count);
        this.rememberProfiles(items);
        this.loading.set(false);
        this.loadFailure = null;
        this.errorText.set(null);
      });

    effect((onCleanup) => {
      const noteId = this.noteId();
      const tenantId = this.teamTenantId();
      const actorId = this.currentUser.accountId();
      // A workspace switch can precede the form's updated teamTenantId input.
      this.currentSpace();
      const contextEpoch = ++this.contextEpoch;
      this.refusedAccessKey.set(null);
      this.collaboration.set(null);
      this.loadState.set('loading');
      this.errorText.set(null);
      this.collaborationChange.emit(undefined);
      this.requestedIds.set(null);
      this.requestedDelta = null;
      this.pending.set(false);
      this.loading.set(false);
      this.loadingMore.set(false);
      this.profiles.set(new Map());
      this.roster.set([]);
      this.memberCount.set(0);
      this.memberSearch.set('');
      this.rosterSearch = null;
      this.loadFailure = null;
      this.seenCycles.clear();
      this.initialReadPending = false;
      this.visibleReadPending = false;
      this.refreshAfterInitialRead = false;
      this.refreshAfterVisibleRead = false;
      if (!tenantId || !actorId) return;
      if (this.draft()) {
        this.searchTerms.next({ tenantId, actorId, search: '' });
        return;
      }
      if (!noteId) return;
      this.initialReadPending = true;
      const subscription = this.api.get(noteId, tenantId).subscribe({
        next: ({ collaboration }) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.initialReadPending = false;
          if (this.refreshAfterInitialRead) {
            this.refreshAfterInitialRead = false;
            this.refreshVisibleDetail();
            return;
          }
          untracked(() => this.applyCollaboration(collaboration));
        },
        error: (error: unknown) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.initialReadPending = false;
          if (this.refreshAfterInitialRead) {
            this.refreshAfterInitialRead = false;
            this.refreshVisibleDetail();
            return;
          }
          this.failClosed();
          if (error instanceof HttpErrorResponse && error.status === 403) this.accessLost.emit();
        },
      });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  private teamAccessKey(ownerId: string): string {
    return JSON.stringify([this.teamTenantId(), this.noteId(), ownerId]);
  }

  private isCurrentTeamAccessRequest(noteId: string, tenantId: string, actorId: string, epoch: number): boolean {
    return this.isCurrentRequest(noteId, tenantId, actorId, epoch) && this.currentSpace()?.id === tenantId;
  }

  private changeTeamAccess(
    access: NoteTeamAccess,
    collaboration: NoteCollaborationDto,
    request: { noteId: string; tenantId: string; actorId: string; epoch: number },
  ): void {
    const { noteId, tenantId, actorId, epoch } = request;
    if (this.pending()) return;
    this.pending.set(true);
    this.errorText.set(null);
    this.injector
      .get(TeamNoteAccessClient)
      .setTeamAccess(tenantId, noteId, access, collaboration.revision)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ collaboration: updated }) => {
          if (!this.isCurrentTeamAccessRequest(noteId, tenantId, actorId, epoch)) return;
          this.pending.set(false);
          this.applyCollaboration(updated);
          this.injector.get(ToasterService).show({ text: TEAM_NOTE_ACCESS_TOAST });
        },
        error: (error: unknown) => {
          if (!this.isCurrentTeamAccessRequest(noteId, tenantId, actorId, epoch)) return;
          const message = teamNoteAccessError(error);
          if (
            error instanceof HttpErrorResponse &&
            error.status === 409 &&
            message === 'Collaboration changed; reload and retry'
          ) {
            this.handleWriteError(error);
            return;
          }
          this.pending.set(false);
          this.errorText.set(message);
          if (error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404)) {
            this.refusedAccessKey.set(this.teamAccessKey(collaboration.ownerAccountUid));
          }
        },
      });
  }

  protected searchMembers(search: string, force = false): void {
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    if (!tenantId || !actorId || (!force && search === this.rosterSearch)) return;
    this.rosterSearch = search;
    this.memberSearch.set(search);
    this.roster.set([]);
    this.memberCount.set(0);
    this.loadFailure = null;
    this.errorText.set(null);
    this.searchTerms.next({ tenantId, actorId, search });
  }

  protected loadMoreMembers(): void {
    const tenantId = this.teamTenantId();
    if (!tenantId || this.loading() || this.loadingMore() || !this.hasMore()) return;
    const search = this.memberSearch();
    const actorId = this.currentUser.accountId();
    const offset = this.roster().length;
    this.loadingMore.set(true);
    this.api
      .members(tenantId, search, offset)
      .pipe(take(1))
      .subscribe({
        next: ({ items, count }) => {
          if (
            tenantId !== this.teamTenantId() ||
            actorId !== this.currentUser.accountId() ||
            search !== this.memberSearch()
          )
            return;
          this.loadingMore.set(false);
          this.roster.update((current) => [
            ...current,
            ...items.filter((item) => !current.some((seen) => seen.accountUid === item.accountUid)),
          ]);
          this.memberCount.set(count);
          this.rememberProfiles(items);
          this.loadFailure = null;
          this.errorText.set(null);
        },
        error: () => {
          if (tenantId !== this.teamTenantId() || actorId !== this.currentUser.accountId()) return;
          this.loadingMore.set(false);
          this.loadFailure = 'memberPage';
          this.errorText.set('Could not load more team members.');
        },
      });
  }

  protected selectAssignees(assigneeIds: readonly string[]): void {
    if (this.draft() && this.draftLocked()) return;
    if (this.draft()) {
      this.draftIds.set(assigneeIds);
      this.draftAssigneesChange.emit(assigneeIds);
      return;
    }
    const collaboration = this.collaboration();
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    if (!collaboration?.capabilities.canAssign || !noteId || !tenantId || !actorId || this.pending()) return;
    const before = new Set(collaboration.assignees.map((item) => item.accountUid));
    const desired = new Set(assigneeIds);
    this.requestedDelta = {
      added: assigneeIds.filter((id) => !before.has(id)),
      removed: [...before].filter((id) => !desired.has(id)),
    };
    this.requestedIds.set(assigneeIds);
    this.pending.set(true);
    this.errorText.set(null);
    this.api.setAssignees(noteId, tenantId, assigneeIds, collaboration.revision).subscribe({
      next: ({ collaboration: updated }) => {
        if (!this.isCurrentRequest(noteId, tenantId, actorId)) return;
        this.pending.set(false);
        this.requestedIds.set(null);
        this.requestedDelta = null;
        this.applyCollaboration(updated);
      },
      error: (error: unknown) => {
        if (this.isCurrentRequest(noteId, tenantId, actorId)) this.handleWriteError(error);
      },
    });
  }

  public retry(): void {
    if (this.loadState() === 'error') {
      const noteId = this.noteId();
      const tenantId = this.teamTenantId();
      if (noteId && tenantId) this.reload(noteId, tenantId);
      return;
    }
    const requested = this.requestedIds();
    if (requested) {
      this.selectAssignees(requested);
      return;
    }
    if (this.loadFailure === 'members') {
      this.searchMembers(this.memberSearch(), true);
      return;
    }
    if (this.loadFailure === 'memberPage') {
      this.loadFailure = null;
      this.errorText.set(null);
      this.loadMoreMembers();
      return;
    }
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    if (noteId && tenantId) this.reload(noteId, tenantId);
    else this.searchMembers('');
  }

  protected closeAccessPanel(): void {
    this.closeAccess.next();
    this.accessTrigger()?.focus();
  }

  public changeAccess(access: NoteTeamAccess): void {
    if (this.draft()) {
      if (this.draftLocked()) return;
      this.draftAccess.set(access);
      this.draftAccessChange.emit(access);
      return;
    }
    const collaboration = this.collaboration();
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    const contextEpoch = this.contextEpoch;
    if (
      collaboration &&
      !collaboration.capabilities.canChangeAccess &&
      this.teamAdminAccess() &&
      noteId &&
      tenantId &&
      actorId
    ) {
      this.changeTeamAccess(access, collaboration, { noteId, tenantId, actorId, epoch: contextEpoch });
      return;
    }
    if (!collaboration?.capabilities.canChangeAccess || !noteId || !tenantId || !actorId || this.pending()) return;
    this.pending.set(true);
    this.api
      .setAccess(noteId, tenantId, access, collaboration.revision)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ collaboration: updated }) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.pending.set(false);
          this.applyCollaboration(updated);
        },
        error: (error: unknown) => {
          if (this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) this.handleWriteError(error);
        },
      });
  }

  protected setResolved(resolved: boolean): void {
    const collaboration = this.collaboration();
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    if (!collaboration || !noteId || !tenantId || !actorId || this.pending()) return;
    if (resolved && !collaboration.capabilities.canResolve) return;
    if (!resolved && !collaboration.capabilities.canReopen) return;
    this.pending.set(true);
    this.api.setResolved(noteId, tenantId, resolved, collaboration.revision).subscribe({
      next: ({ collaboration: updated }) => {
        if (!this.isCurrentRequest(noteId, tenantId, actorId)) return;
        this.pending.set(false);
        this.applyCollaboration(updated);
      },
      error: (error: unknown) => {
        if (this.isCurrentRequest(noteId, tenantId, actorId)) this.handleWriteError(error);
      },
    });
  }

  private applyCollaboration(collaboration: NoteCollaborationDto | null): void {
    const couldAssign = untracked(() => this.collaboration()?.capabilities.canAssign ?? false);
    if (this.loadFailure === 'collaboration') {
      this.loadFailure = null;
      this.errorText.set(null);
    }
    this.collaboration.set(collaboration);
    this.loadState.set(collaboration ? 'ready' : 'legacy');
    this.collaborationChange.emit(collaboration);
    if (!collaboration) return;
    this.rememberProfiles(collaboration.assignees.flatMap(({ profile }) => (profile ? [profile] : [])));
    if (collaboration.ownerProfile) this.rememberProfiles([collaboration.ownerProfile]);
    if (!collaboration.capabilities.canAssign && couldAssign) {
      this.rosterSearch = null;
      this.roster.set([]);
      this.memberCount.set(0);
      this.loading.set(false);
      this.loadingMore.set(false);
      if (this.loadFailure === 'members' || this.loadFailure === 'memberPage') {
        this.loadFailure = null;
        this.errorText.set(null);
      }
    }
    if (collaboration.capabilities.canAssign && !couldAssign) this.searchMembers(this.memberSearch());
    this.markSeenIfViewed();
  }

  private observeDetailRegion(): void {
    if (this.destroyed || !this.host.nativeElement.isConnected) return;
    const region = this.host.nativeElement.closest('app-note-form')?.querySelector<HTMLElement>('.form') ?? null;
    if (!region || region === this.detailRegion || typeof IntersectionObserver === 'undefined') return;
    this.detailObserver?.disconnect();
    this.detailRegion = region;
    this.detailIntersecting = false;
    this.detailObserved = false;
    this.detailObserver = new IntersectionObserver((entries) => {
      if (this.destroyed || this.detailRegion !== region) return;
      const entry = entries.find((candidate) => candidate.target === region);
      if (!entry) return;
      const wasIntersecting = this.detailIntersecting;
      const hadObservation = this.detailObserved;
      this.detailObserved = true;
      this.detailIntersecting = entry.isIntersecting;
      if (!this.isDetailViewed()) return;
      if (hadObservation && !wasIntersecting) this.refreshVisibleDetail();
      else this.markSeenIfViewed();
    });
    this.detailObserver.observe(region);
  }

  private isDetailViewed(): boolean {
    if (
      this.destroyed ||
      !this.host.nativeElement.isConnected ||
      this.document.visibilityState !== 'visible' ||
      !this.detailIntersecting ||
      !this.detailRegion?.isConnected
    ) {
      return false;
    }
    for (let element: HTMLElement | null = this.detailRegion; element; element = element.parentElement) {
      const style = this.document.defaultView?.getComputedStyle(element);
      if (style?.display === 'none' || style?.visibility === 'hidden' || style?.opacity === '0') return false;
    }
    return true;
  }

  private refreshVisibleDetail(): void {
    if (!this.isDetailViewed()) return;
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    const contextEpoch = this.contextEpoch;
    if (!noteId || !tenantId || !actorId || this.draft()) return;
    if (this.initialReadPending) {
      this.refreshAfterInitialRead = true;
      return;
    }
    if (this.visibleReadPending) {
      this.refreshAfterVisibleRead = true;
      return;
    }
    this.visibleReadPending = true;
    this.api
      .get(noteId, tenantId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ collaboration }) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.visibleReadPending = false;
          if (this.refreshAfterVisibleRead) {
            this.refreshAfterVisibleRead = false;
            this.refreshVisibleDetail();
            return;
          }
          this.applyCollaboration(collaboration);
        },
        error: (error: unknown) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.visibleReadPending = false;
          if (this.refreshAfterVisibleRead) {
            this.refreshAfterVisibleRead = false;
            this.refreshVisibleDetail();
            return;
          }
          this.failClosed();
          if (error instanceof HttpErrorResponse && error.status === 403) this.accessLost.emit();
        },
      });
  }

  private markSeenIfViewed(): void {
    if (!this.isDetailViewed()) return;
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    const actorId = this.currentUser.accountId();
    const contextEpoch = this.contextEpoch;
    const collaboration = this.collaboration();
    const assignmentUid = collaboration?.viewerAssignmentUid;
    if (
      !noteId ||
      !tenantId ||
      !actorId ||
      !assignmentUid ||
      collaboration.viewerSeenAt ||
      this.seenCycles.has(assignmentUid)
    ) {
      return;
    }
    this.seenCycles.add(assignmentUid);
    this.api
      .markSeen(noteId, tenantId, assignmentUid)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ collaboration: seenState }) => {
          if (
            this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch) &&
            this.collaboration()?.viewerAssignmentUid === assignmentUid
          ) {
            this.applyCollaboration(seenState);
          }
        },
        error: () => {
          if (this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) this.seenCycles.delete(assignmentUid);
        },
      });
  }

  private rememberProfiles(profiles: readonly NoteMemberProfileDto[]): void {
    this.profiles.update((current) => {
      const next = new Map(current);
      for (const profile of profiles) next.set(profile.accountUid, profile);
      return next;
    });
  }

  private reload(noteId: string, tenantId: string): void {
    const actorId = this.currentUser.accountId();
    const contextEpoch = this.contextEpoch;
    if (this.loadState() !== 'ready') {
      this.loadState.set('loading');
      this.collaboration.set(null);
      this.collaborationChange.emit(undefined);
    }
    this.api
      .get(noteId, tenantId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ collaboration }) => {
          if (!this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) return;
          this.applyCollaboration(collaboration);
          if (collaboration && this.requestedDelta) {
            const merged = new Set(collaboration.assignees.map((item) => item.accountUid));
            for (const id of this.requestedDelta.added) merged.add(id);
            for (const id of this.requestedDelta.removed) merged.delete(id);
            this.requestedIds.set([...merged]);
          }
        },
        error: () => {
          if (this.isCurrentRequest(noteId, tenantId, actorId, contextEpoch)) this.failClosed();
        },
      });
  }

  private failClosed(): void {
    this.collaboration.set(null);
    this.loadState.set('error');
    this.collaborationChange.emit(undefined);
    this.loadFailure = 'collaboration';
    this.errorText.set('Could not load assignment details.');
  }

  private isCurrentRequest(noteId: string, tenantId: string, actorId: string | null, contextEpoch?: number): boolean {
    return (
      !this.destroyed &&
      this.host.nativeElement.isConnected &&
      (contextEpoch === undefined || contextEpoch === this.contextEpoch) &&
      noteId === this.noteId() &&
      tenantId === this.teamTenantId() &&
      actorId === this.currentUser.accountId()
    );
  }

  private handleWriteError(error: unknown): void {
    this.pending.set(false);
    if (error instanceof HttpErrorResponse && error.status === 403) {
      this.failClosed();
      this.accessLost.emit();
      return;
    }
    this.loadFailure = null;
    this.errorText.set(
      error instanceof HttpErrorResponse && error.status === 409
        ? 'Note changed. Refresh and retry your selection.'
        : 'Could not save assignment. Retry.',
    );
    const noteId = this.noteId();
    const tenantId = this.teamTenantId();
    if (noteId && tenantId && error instanceof HttpErrorResponse && error.status === 409) {
      this.reload(noteId, tenantId);
    }
  }
}
